<script setup lang="ts">
import type { PrepareFormValues } from './useUploadSessionFormWizard';

import { debounce } from 'lodash';
import pluralize from 'pluralize';

import { useFilters } from '@/hooks/useFilters';
import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import Distiller from '@/components/distiller/Distiller.vue';
import DistillerEmptyState from '@/components/distiller/DistillerEmptyState.vue';
import Divider from '@/components/divider/Divider.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FilterProvider from '@/components/distiller/FilterProvider.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormSwitchInput from '@/components/formSwitchInput/FormSwitchInput.vue';

import SpinnerIcon from '@/icons/line/spinner.svg';

const props = defineProps<{
  uploadType: App.Bulkinator.Data.UploadTypeData;
  distiller: App.Search.Data.DistillerData;
  form: ReturnType<typeof useForm<PrepareFormValues>>;
}>();
const emit = defineEmits<{ cancel: [void]; submit: [void] }>();

const {
  filteredRecordsCount,
  formattedFilteredRecordsCount,
  getRecordCount,
  isLoadingRecordCount
} = useFilters();

const refreshRecordCount = debounce(() => {
  getRecordCount(props.distiller, props.form.fields.filters);
}, 350);

watch(() => props.form.fields.filters, refreshRecordCount, { deep: true });

const description = computed(() => {
  return 'Apply filters to limit which records are included in the upload.';
});

const hasAppliedFilter = computed(
  () => props.form.fields.filters && Object.values(props.form.fields.filters).some((v) => !!v)
);

const addingNewFilter = ref(false);
</script>

<template>
  <Form @submit.prevent="emit('submit')">
    <Fieldset title="Create Empty Session">
      <FormSwitchInput
        v-model:checked="form.fields.is_empty_session"
        helperText="This will create a new upload session with no rows in it."
        label="Create an empty session"
        name="is_empty_session"
      />
    </Fieldset>
    <template v-if="!form.fields.is_empty_session">
      <Divider />
      <Fieldset title="Filters">
        <FilterProvider v-model="form.fields.filters">
          <DistillerEmptyState
            :description="description"
            :addingNewFilter="addingNewFilter"
            @update:addingNewFilter="addingNewFilter = $event"
          >
            <Distiller
              :distiller="distiller"
              title="Filters"
              v-model:addingNewFilter="addingNewFilter"
            />
          </DistillerEmptyState>
        </FilterProvider>
        <div class="flex items-center gap-2 pt-2 text-sm text-zinc-700">
          The selected filters will create an upload session with
          <SpinnerIcon
            v-if="isLoadingRecordCount"
            class="h-4 w-4 animate-spin text-secondary-900"
          />
          <template v-else>
            {{ formattedFilteredRecordsCount }}
            {{ pluralize(distiller.name?.label ?? 'Record', filteredRecordsCount?.count) }}
          </template>
          .
        </div>
      </Fieldset>
    </template>

    <template #footer>
      <FormFooter>
        <Button
          :color="ButtonColor.primary"
          :disabled="!form.fields.is_empty_session && !hasAppliedFilter"
          :variant="ButtonVariant.solid"
          type="submit"
          >Continue</Button
        >
        <Button
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
          @click="$emit('cancel')"
          >Previous</Button
        >
      </FormFooter>
    </template>
  </Form>
</template>
